import { motion } from 'framer-motion';
import { Mail, MessageCircle, Shield, Zap, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  return (
    <footer className="relative bg-black border-t border-purple-900/30 pt-20 pb-10 px-4">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto">
        {/* Main content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* About */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl flex items-center justify-center font-black text-white text-2xl">
                C
              </div>
              <div>
                <div className="text-2xl font-black text-white">QUANTUM SHOP</div>
                <div className="text-xs text-purple-400">Premium Roblox Items</div>
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed">
              Your trusted marketplace for premium Roblox game items. We specialize in Pet Simulator 99, Adopt Me, and Murder Mystery 2.
            </p>
          </motion.div>

          {/* What We Sell */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h3 className="text-xl font-black text-white mb-6">What We Sell</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-purple-500 font-bold mb-2">Pet Simulator 99 Stock</h4>
                <ul className="text-sm text-gray-400 space-y-1">
                  <li>• Titanics</li>
                  <li>• Gargantuans</li>
                  <li>• Huges</li>
                  <li>• Gems</li>
                </ul>
              </div>
              <div>
                <h4 className="text-purple-500 font-bold mb-2">Adopt Me Stock</h4>
                <ul className="text-sm text-gray-400 space-y-1">
                  <li>• High Tiers</li>
                  <li>• Mid Tiers</li>
                  <li>• Low Tiers</li>
                  <li>• Pet Wear</li>
                </ul>
              </div>
              <div>
                <h4 className="text-purple-500 font-bold mb-2">Murder Mystery 2 Stock</h4>
                <ul className="text-sm text-gray-400 space-y-1">
                  <li>• Godlies & Chromas</li>
                  <li>• Vintage & Ancients</li>
                  <li>• Exclusive Sets</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Trust & Features */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="text-xl font-black text-white mb-6">Why Choose Us</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <h4 className="text-white font-bold mb-1">Instant Delivery</h4>
                  <p className="text-sm text-gray-400">Get your items within minutes</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <h4 className="text-white font-bold mb-1">Secure Trading</h4>
                  <p className="text-sm text-gray-400">Safe and protected transactions</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Star className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <h4 className="text-white font-bold mb-1">Premium Quality</h4>
                  <p className="text-sm text-gray-400">Best items at competitive prices</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <h4 className="text-white font-bold mb-1">24/7 Support</h4>
                  <p className="text-sm text-gray-400">Always here to help you</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-purple-900/30 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm">
            © 2025 Quantum Shop. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="text-gray-400 hover:text-purple-500 transition-colors text-sm" data-testid="footer-terms-link">
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-gray-400 hover:text-purple-500 transition-colors text-sm" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/contact" className="text-gray-400 hover:text-purple-500 transition-colors text-sm flex items-center gap-2" data-testid="footer-contact-link">
              <Mail className="w-4 h-4" />
              Contact
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
